import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';
import Editor from '@monaco-editor/react';
import { MdArrowBack, MdSave, MdRefresh } from 'react-icons/md';
import './ConfigEditor.css';

const API_URL = 'https://j1jjasmb97.execute-api.eu-north-1.amazonaws.com/LoadLogsAndConfigurations-API';
const SAVE_API_URL = 'https://j1jjasmb97.execute-api.eu-north-1.amazonaws.com/SaveConfigs-API';

const CONFIG_TABS = [
  { key: 'basic', label: 'Basic Settings' },
  { key: 'heatingcontrol_configs', label: 'Heating Control' },
  { key: 'aircontrol_configs', label: 'Air Control' },
  { key: 'consumptioncontrol_configs', label: 'Consumption Control' },
  { key: 'pvcontrol_configs', label: 'PV Control' },
];

function ConfigEditor() {
  const { accountName } = useParams();
  const decodedAccountName = decodeURIComponent(accountName);
  const { jwtToken, isAdmin } = useUser();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [fullConfig, setFullConfig] = useState(null);
  const [editedConfigs, setEditedConfigs] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const editorRefs = useRef({});

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchConfig();
  }, [isAdmin, accountName, jwtToken]);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          account_name: decodedAccountName,
          full_config: true
        })
      });

      const data = await response.json();

      if (response.ok && data.full_config) {
        setFullConfig(data.full_config);
        // Initialize edited configs from fetched data
        const initial = {};
        CONFIG_TABS.forEach(tab => {
          if (tab.key === 'basic') {
            // Extract all boolean fields and text fields for basic settings
            initial.basic = {
              display_name: data.full_config.display_name || '',
              location: data.full_config.location || '',
              enabled: data.full_config.enabled || false,
              beta: data.full_config.beta || false,
              monitoring_enabled: data.full_config.monitoring_enabled || false,
              heating_control_enabled: data.full_config.heating_control_enabled || false,
              consumption_control_enabled: data.full_config.consumption_control_enabled || false,
              triggers_enabled: data.full_config.triggers_enabled || false,
            };
          } else if (data.full_config[tab.key]) {
            initial[tab.key] = data.full_config[tab.key];
          }
        });
        setEditedConfigs(initial);
        setHasChanges(false);
        setValidationErrors({});
      } else {
        setError(data.error || 'Failed to fetch configuration');
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (tabKey, value) => {
    // For JSON tabs, validate the JSON
    if (tabKey !== 'basic') {
      try {
        JSON.parse(value);
        setValidationErrors(prev => ({ ...prev, [tabKey]: null }));
      } catch (e) {
        setValidationErrors(prev => ({ ...prev, [tabKey]: e.message }));
      }
    }

    setEditedConfigs(prev => ({
      ...prev,
      [tabKey]: tabKey === 'basic' ? prev[tabKey] : value
    }));
    setHasChanges(true);
  };

  const handleBasicChange = (field, value) => {
    setEditedConfigs(prev => ({
      ...prev,
      basic: {
        ...prev.basic,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Check for validation errors
    const hasErrors = Object.values(validationErrors).some(err => err !== null);
    if (hasErrors) {
      alert('Please fix JSON validation errors before saving');
      return;
    }

    setSaving(true);

    try {
      // Build the update payload
      const updates = {};

      // Basic settings
      if (editedConfigs.basic) {
        updates.display_name = editedConfigs.basic.display_name;
        updates.location = editedConfigs.basic.location;
        updates.enabled = editedConfigs.basic.enabled;
        updates.beta = editedConfigs.basic.beta;
        updates.monitoring_enabled = editedConfigs.basic.monitoring_enabled;
        updates.heating_control_enabled = editedConfigs.basic.heating_control_enabled;
        updates.consumption_control_enabled = editedConfigs.basic.consumption_control_enabled;
        updates.triggers_enabled = editedConfigs.basic.triggers_enabled;
      }

      // JSON config sections - stringify them
      CONFIG_TABS.forEach(tab => {
        if (tab.key !== 'basic' && editedConfigs[tab.key]) {
          const configValue = typeof editedConfigs[tab.key] === 'string'
            ? editedConfigs[tab.key]
            : JSON.stringify(editedConfigs[tab.key]);
          updates[tab.key] = configValue;
        }
      });

      const response = await fetch(SAVE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          account_name: decodedAccountName,
          raw_config_mode: true,  // Use raw config replacement for admin editor
          ...updates
        })
      });

      const data = await response.json();

      if (response.ok) {
        setHasChanges(false);
        // Refresh config to get updated values
        await fetchConfig();
        alert('Configuration saved successfully');
      } else {
        alert(`Failed to save: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error saving config:', err);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    navigate('/admin');
  };

  const getEditorValue = (tabKey) => {
    if (tabKey === 'basic') return null;

    const config = editedConfigs[tabKey];
    if (!config) return '{}';

    if (typeof config === 'string') {
      return config;
    }
    return JSON.stringify(config, null, 2);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="config-editor-container">
      <div className="config-editor-header">
        <button className="config-back-btn" onClick={handleBack}>
          <MdArrowBack /> Back
        </button>
        <div className="config-header-info">
          <h1>Configuration Editor</h1>
          <p>{decodedAccountName}</p>
        </div>
        <div className="config-header-actions">
          <button
            className="config-refresh-btn"
            onClick={fetchConfig}
            disabled={loading}
            title="Refresh configuration"
          >
            <MdRefresh />
          </button>
          <button
            className="config-save-btn"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            <MdSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="config-loading">Loading configuration...</div>
      )}

      {error && (
        <div className="config-error">
          <p>{error}</p>
          <button onClick={fetchConfig}>Retry</button>
        </div>
      )}

      {!loading && !error && fullConfig && (
        <>
          <div className="config-tabs">
            {CONFIG_TABS.map(tab => (
              <button
                key={tab.key}
                className={`config-tab ${activeTab === tab.key ? 'active' : ''} ${validationErrors[tab.key] ? 'has-error' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {validationErrors[tab.key] && <span className="error-indicator">!</span>}
              </button>
            ))}
          </div>

          <div className="config-editor-content">
            {activeTab === 'basic' ? (
              <div className="config-basic-settings">
                <div className="config-field">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={editedConfigs.basic?.display_name || ''}
                    onChange={(e) => handleBasicChange('display_name', e.target.value)}
                    placeholder="e.g., Home"
                  />
                  <span className="field-description">Friendly name shown in the UI</span>
                </div>

                <div className="config-field">
                  <label>Location</label>
                  <input
                    type="text"
                    value={editedConfigs.basic?.location || ''}
                    onChange={(e) => handleBasicChange('location', e.target.value)}
                    placeholder="e.g., Helsinki"
                  />
                  <span className="field-description">Location for weather data</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.enabled || false}
                      onChange={(e) => handleBasicChange('enabled', e.target.checked)}
                    />
                    Enabled
                  </label>
                  <span className="field-description">Account is active and controls are running</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.beta || false}
                      onChange={(e) => handleBasicChange('beta', e.target.checked)}
                    />
                    Beta
                  </label>
                  <span className="field-description">Use beta versions of control functions</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.monitoring_enabled || false}
                      onChange={(e) => handleBasicChange('monitoring_enabled', e.target.checked)}
                    />
                    Monitoring Enabled
                  </label>
                  <span className="field-description">Enable monitoring control module</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.heating_control_enabled || false}
                      onChange={(e) => handleBasicChange('heating_control_enabled', e.target.checked)}
                    />
                    Heating Control Enabled
                  </label>
                  <span className="field-description">Enable heating control module</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.consumption_control_enabled || false}
                      onChange={(e) => handleBasicChange('consumption_control_enabled', e.target.checked)}
                    />
                    Consumption Control Enabled
                  </label>
                  <span className="field-description">Enable consumption control module</span>
                </div>

                <div className="config-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedConfigs.basic?.triggers_enabled || false}
                      onChange={(e) => handleBasicChange('triggers_enabled', e.target.checked)}
                    />
                    Triggers Enabled
                  </label>
                  <span className="field-description">Enable webhook triggers</span>
                </div>
              </div>
            ) : (
              <div className="config-monaco-wrapper">
                {validationErrors[activeTab] && (
                  <div className="config-validation-error">
                    JSON Error: {validationErrors[activeTab]}
                  </div>
                )}
                <Editor
                  height="calc(100vh - 280px)"
                  defaultLanguage="json"
                  value={getEditorValue(activeTab)}
                  onChange={(value) => handleEditorChange(activeTab, value)}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    wordWrap: 'on',
                    formatOnPaste: true,
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    tabSize: 2
                  }}
                  onMount={(editor) => {
                    editorRefs.current[activeTab] = editor;
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}

      {hasChanges && (
        <div className="config-unsaved-indicator">
          Unsaved changes
        </div>
      )}
    </div>
  );
}

export default ConfigEditor;
